import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';

import { useGlobalContext } from '../../../context/useGlobalContext';
import { UserDetailsResponse } from '../../../listoramaAdmin-api/generated-src/api';
import ListORamaAdminApiFactory from '../../../listoramaAdmin-api/ListORamaAdminApiFactory';
import { validateCustomerId } from '../../../utils/validation';
import { ListEditData, ListModalData } from '../..//components/ListActionModal/ListActionModal';
import { UserModalData } from '../../components/UserActionModal/UserActionModal';
import { SelectedLists } from './components/UserListsDataTable/UserListsDataTable';
import { UserDetailsSectionView } from './UserDetailsSectionView';

export interface UserDetails {
    data?: UserDetailsResponse;
    fetching: boolean;
    fetchingMessage?: string;
    error?: AxiosError;
}

const LISTS_PER_LOAD = 250;

export const UserDetailsSection: React.FC = () => {
    const { searchInput } = useGlobalContext();
    const [userDetails, setUserDetails] = useState<UserDetails>({ fetching: false });
    const [listEditData, setListEditData] = useState<ListEditData>({});
    const [selectedLists, setSelectedLists] = useState<SelectedLists>({});
    const [userModalData, setUserModalData] = useState<UserModalData>({ isOpen: false });
    const [listModalData, setListModalData] = useState<ListModalData>({ isOpen: false });
    const [canLoadMore, setCanLoadMore] = useState<boolean>(false);

    const createSelectedLists = (data: UserDetailsResponse, selectedLists_: SelectedLists) => {
        data.user.lists.forEach((data) => {
            if (!(data.listId in selectedLists_)) {
                selectedLists_[data.listId] = false;
            }
        });
        setSelectedLists(selectedLists_);
    };

    useEffect(() => {
        if (searchInput) {
            fetchUserDetails(searchInput, false);
        }
    }, [searchInput]);

    const onLoadMore = async () => {
        fetchUserDetails(searchInput, true);
    };

    const fetchUserDetails = async (authorId: string, isLoadingMore: boolean) => {
        try {
            if (!validateCustomerId(authorId)) {
                const error = new Error(`${authorId} is not a valid Customer Id. Please try again.`);
                error.name = 'Bad Request';
                throw AxiosError.from(error);
            }
            setUserModalData({ isOpen: false });
            setListModalData({ isOpen: false });
            if (!isLoadingMore) {
                setUserDetails({
                    fetching: true,
                    fetchingMessage: `Loading user list details for ${authorId}...`,
                    error: undefined
                });
                const response = await ListORamaAdminApiFactory().userDetails.userDetails(authorId, LISTS_PER_LOAD);
                createSelectedLists(response.data, {}); //set selected lists to empty object on initial load
                setUserDetails((prevState) => ({ ...prevState, data: response.data, fetching: false }));
                //if the user has less lists than we requested, we can assume there are no more lists to load
                setCanLoadMore(response.data.user.lists.length === LISTS_PER_LOAD);
            } else if (userDetails.data !== undefined) {
                //If the data is undefined, we shouldn't be loading more
                setUserDetails((prevState) => ({
                    ...prevState,
                    fetching: true,
                    fetchingMessage: `Loading more user list details for ${authorId}...`,
                    error: undefined
                }));
                const response = await ListORamaAdminApiFactory().userDetails.userDetails(
                    authorId,
                    LISTS_PER_LOAD,
                    userDetails.data.user.lists.length
                );
                userDetails.data.user.lists.push(...response.data.user.lists);
                createSelectedLists(userDetails.data, selectedLists);
                setUserDetails((prevState) => ({ ...prevState, data: userDetails.data, fetching: false }));
                //if we get back less additional lists that we requested, we can assume there are no more lists to load
                setCanLoadMore(response.data.user.lists.length === LISTS_PER_LOAD);
            } else {
                // eslint-disable-next-line no-console
                console.error(
                    'An attempt was made to load more data for an undefined request, please reach out the dev team to report this bug'
                );
            }
        } catch (e) {
            setUserDetails({ error: e as AxiosError, fetching: false });
        }
    };

    const addListFlagBatch = async (data: ListEditData) => {
        try {
            if (data.flag && userDetails.data) {
                setUserDetails({
                    fetching: true,
                    fetchingMessage: `Adding ${data.flag} flag to lists for ${searchInput}...`
                });
                await ListORamaAdminApiFactory().addListFlagBatch.adminBatchAddListFlag({
                    customerId: userDetails.data.user.author.authorId,
                    listIds: Object.keys(selectedLists).filter((listId) => selectedLists[listId]),
                    listFlag: data.flag
                });
                setListEditData({ flag: undefined });
                fetchUserDetails(searchInput, false);
            }
        } catch (e) {
            setUserDetails({ error: e as AxiosError, fetching: false });
        }
    };

    const removeListFlagBatch = async (data: ListEditData) => {
        try {
            if (data.flag && userDetails.data) {
                setUserDetails({
                    fetching: true,
                    fetchingMessage: `Removing ${data.flag} flag for lists for ${searchInput}...`
                });
                await ListORamaAdminApiFactory().removeListFlagBatch.adminBatchRemoveListFlag({
                    customerId: userDetails.data.user.author.authorId,
                    listIds: Object.keys(selectedLists).filter((listId) => selectedLists[listId]),
                    listFlag: data.flag
                });
                setListEditData({ flag: undefined });
                fetchUserDetails(searchInput, false);
            }
        } catch (e) {
            setUserDetails({ error: e as AxiosError, fetching: false });
        }
    };

    const editListStateBatch = async (data: ListEditData) => {
        try {
            if (data.state && userDetails.data) {
                setUserDetails({
                    fetching: true,
                    fetchingMessage: `Setting state to ${data.state} for lists for ${searchInput}...`
                });
                await ListORamaAdminApiFactory().editListStateBatch.adminEditListStateBatch({
                    customerId: userDetails.data.user.author.authorId,
                    listIds: Object.keys(selectedLists).filter((listId) => selectedLists[listId]),
                    state: data.state
                });
                setListEditData({ state: undefined });
                fetchUserDetails(searchInput, false);
            }
        } catch (e) {
            setUserDetails({ error: e as AxiosError, fetching: false });
        }
    };

    const setListRestrictionStatus = async (userStatus: boolean) => {
        const userAlias = window.sessionStorage.getItem('userAlias');
        if (!userAlias) {
            throw new Error('User alias not found! Please refresh page') as AxiosError;
        }

        try {
            if (userDetails.data) {
                await ListORamaAdminApiFactory().setListRestriction.adminSetListRestriction({
                    status: userStatus,
                    customerId: userDetails.data.user.author.authorId,
                    operator: userAlias
                });
                fetchUserDetails(searchInput, false);
            }
        } catch (e) {
            setUserDetails({ error: e as AxiosError, fetching: false });
        }
    };

    return (
        <UserDetailsSectionView
            searchInput={searchInput}
            userDetails={userDetails}
            listEditData={listEditData}
            setListEditData={setListEditData}
            selectedLists={selectedLists}
            setSelectedLists={setSelectedLists}
            userModalData={userModalData}
            setUserModalData={setUserModalData}
            listModalData={listModalData}
            setListRestrcitionStatus={setListRestrictionStatus}
            setListModalData={setListModalData}
            editListStateBatch={editListStateBatch}
            addListFlagBatch={addListFlagBatch}
            removeListFlagBatch={removeListFlagBatch}
            canLoadMore={canLoadMore}
            onLoadMore={onLoadMore}
        />
    );
};
